import React, { useEffect, useState } from 'react';
import { Alert, Card, CardBody, CardHeader, Collapse } from 'reactstrap';

import { getErrorMessage } from '~libs/axiosWrapper';

import { ErrorResponse, Spinner } from '~components';
import Icon, { faChevronDown, faChevronUp } from '~components/icon';
import { TableView } from '~components/table-view';

import { rollbackAffectedColumns, rollbackErrorColumns } from '~support-at-home/forms/statements/estimateLists';

import { RollbackInvite, useRollbackEstimateQuery } from '~support-at-home/actions/statements';

import { DateRange, ErrorDetailsCard, SummaryStatCard, SummaryStatCluster } from '../common';

interface ComponentProps {
  token: string;
  invite: RollbackInvite;
  onChange?: (name: string, value: any) => void;
}

const statementsLabel = (count: number) => `${count} ${count === 1 ? 'statement' : 'statements'}`;

const blockedSummary = (blocked: number, total: number) =>
  total === 1
    ? 'This statement cannot be rolled back.'
    : `${blocked} of ${statementsLabel(total)} cannot be rolled back, so none of them can.`;

const readySummary = (total: number) =>
  total === 1 ? 'The statement below will be removed.' : `All ${statementsLabel(total)} below will be removed.`;

// TAV-3491: all or nothing. Shows the counts first, then lists every statement the link covers. If any one of them is
// blocked, nothing can be rolled back and there is no way forward.
const Check: React.FC<ComponentProps> = ({ token, invite, onChange }) => {
  const { data, error, isFetching } = useRollbackEstimateQuery({ invite: token }, { refetchOnMountOrArgChange: true });
  const [isListOpen, setIsListOpen] = useState(false);

  // Hand the checked statements to the parent form. Cleared while a new check loads, so stale ids can
  // never reach the confirm window.
  useEffect(() => {
    onChange?.('rollback_check', !isFetching && data ? { data } : null);
  }, [data, isFetching]);

  const { statements, items, budgets, affected = [], errors = [] } = data || {};
  const { total = 0, blocked = 0 } = statements || {};

  return (
    <div>
      <DateRange start_date={invite.start_date} end_date={invite.end_date} title="Statement Period" />
      <Spinner loading={isFetching} />
      {error && <ErrorResponse message={getErrorMessage(error)} dataCy="statements-rollback-check-error" />}
      {data && !isFetching && (
        <>
          {total === 0 && <Alert color="info">No statements were found for this month.</Alert>}
          {total > 0 && !data.can_roll_back && (
            <Alert color="danger" data-cy="statements-rollback-blocked">
              <strong>Nothing can be rolled back.</strong> {blockedSummary(blocked, total)} Contact TurnPoint Support.
            </Alert>
          )}
          {data.can_roll_back && (
            <Alert color="warning" data-cy="statements-rollback-ready">
              <strong>{readySummary(total)}</strong> Open the list of statements to check it before you continue.
            </Alert>
          )}
          {data.can_roll_back && (
            <div className="d-flex flex-wrap mb-4 gap-4" data-cy="statements-rollback-counts">
              <SummaryStatCluster title="Statements">
                <SummaryStatCard count={total} countLabel="To Roll Back" countClassName="text-danger" />
              </SummaryStatCluster>
              <SummaryStatCluster title="Will Be Removed / Cleared">
                <SummaryStatCard count={items?.to_release ?? 0} countLabel="Reconciled Items Cleared" />
                <SummaryStatCard count={budgets?.statement_budgets ?? 0} countLabel="Budget Snapshots" />
                <SummaryStatCard count={budgets?.prior_spent_resets ?? 0} countLabel="Prior Spend Reset" />
              </SummaryStatCluster>
            </div>
          )}
          {errors.length > 0 && (
            <ErrorDetailsCard
              id="statements-rollback-blockers"
              title="Cannot Roll Back"
              errors={errors}
              columns={rollbackErrorColumns}
              defaultOpen
            />
          )}
          {affected.length > 0 && (
            // Starts closed: a link can cover hundreds of statements, and the counts above must stay in view
            <Card className="rounded-0">
              <CardHeader
                className="d-flex justify-content-between border-0 fw-bold align-items-center"
                type="button"
                onClick={() => setIsListOpen(!isListOpen)}
                data-cy="statements-rollback-affected-toggle"
              >
                <div>Statements in This Link ({affected.length})</div>
                <Icon icon={isListOpen ? faChevronUp : faChevronDown} className="ms-2" />
              </CardHeader>
              <Collapse isOpen={isListOpen}>
                <CardBody className="p-0">
                  <TableView
                    id="statements-rollback-affected-table"
                    columns={rollbackAffectedColumns}
                    data={affected}
                    emptyMessage="No statements"
                    readonly
                    striped
                  />
                </CardBody>
              </Collapse>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default Check;
